import React, { FC, ReactNode, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';

// "Zakeke AI" painted in the AI gradient; the drawer/dialog's own title becomes a neutral caption below it.
const AiHeadingTitle = styled.h3`
    margin: 0;
    width: fit-content;
    padding-right: 2px;
    font-family: ${(props) => props.theme.app.font};
    font-size: ${(props) => props.theme.app.component.card.title.size};
    font-weight: ${(props) => props.theme.app.component.card.title.weight};
    background: ${theme.ai.gradientHorizontal};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const AiHeadingSubtitle = styled.div`
    margin-top: 2px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 400;
`;

// The shared Zakeke AI heading, for AI dialogs that render their title inside the content.
export const AiHeading: FC<{ caption: ReactNode; className?: string }> = ({ caption, className }) => (
    <div className={className}>
        <AiHeadingTitle>{T._('Zakeke AI', 'Admin')}</AiHeadingTitle>
        <AiHeadingSubtitle>{caption}</AiHeadingSubtitle>
    </div>
);

// Standard drawer header padding around the shared heading.
const AiDrawerHeaderBar = styled.div`
    padding: 16px 24px;
`;

// Pass to a Drawer's `header` prop to get the shared Zakeke AI header across all AI drawers.
export const aiDrawerHeader = (caption: string): ReactNode => (
    <AiDrawerHeaderBar>
        <AiHeading caption={caption} />
    </AiDrawerHeaderBar>
);

const stepEnter = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
`;
const gradientShift = keyframes`
    0% { background-position: 0% center; }
    100% { background-position: 300% center; }
`;

// One line painted with the moving AI gradient: the "thinking/analyzing" status. Shared by Zakeke AI and the AI drawers,
// so any change to the waiting state shows the same in both places.
export const AiThinkingLine = styled.div`
    width: fit-content;
    padding-right: 2px;
    background: linear-gradient(90deg, #7c3aed, #a855f7, #ec4899, #a855f7, #7c3aed);
    background-size: 300% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 12px;
    font-weight: 500;
    animation:
        ${stepEnter} 0.25s ease-out,
        ${gradientShift} 3s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Rotates through phrases on the shared gradient line, for callers without a live step feed (e.g. the pricing drawer).
export const AiRotatingThinking: FC<{ phrases: string[]; intervalMs?: number }> = ({ phrases, intervalMs = 2500 }) => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (phrases.length <= 1) return;
        const timer = setInterval(() => setIndex((prev) => (prev + 1) % phrases.length), intervalMs);
        return () => clearInterval(timer);
    }, [phrases.length, intervalMs]);
    return <AiThinkingLine key={phrases[index]}>{phrases[index]}</AiThinkingLine>;
};

// Compact pill chips: blue to confirm, yellow to revise.
const CHIP_TONES = {
    confirm: { background: '#0288d1', hover: '#0277bd', color: 'white' },
    revise: { background: '#ffc107', hover: '#ffb300', color: '#263238' }
} as const;

// One-line approval banner: title + message on the left, Revise/Confirm chips on the right. Shared by Zakeke AI and the AI drawers.
const BannerCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ffcc80;
    border-radius: 10px;
    background: #fff8e1;
    font-family: inherit;
    text-align: left;
`;

const BannerText = styled.span`
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    color: #5d4037;
    line-height: 1.3;

    strong {
        color: #e65100;
        font-size: 12px;
    }

    span {
        overflow: hidden;
        font-size: 11.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const BannerActions = styled.div`
    display: flex;
    flex: none;
    gap: 6px;
`;

const BannerChip = styled.button<{ $tone: keyof typeof CHIP_TONES }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 12px;
    border: 0;
    border-radius: 999px;
    background: ${(props) => CHIP_TONES[props.$tone].background};
    color: ${(props) => CHIP_TONES[props.$tone].color};
    font-family: inherit;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => CHIP_TONES[props.$tone].hover};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export interface AiApprovalActionsProps {
    reviseLabel: string;
    confirmLabel: string;
    onRevise: () => void;
    onConfirm: () => void;
    disabled?: boolean;
    className?: string;
}

// The Revise (yellow) / Confirm (blue) chips on their own, so they can be reused outside the banner (e.g. a drawer footer).
export const AiApprovalActions: FC<AiApprovalActionsProps> = ({ reviseLabel, confirmLabel, onRevise, onConfirm, disabled, className }) => (
    <BannerActions className={className}>
        <BannerChip type='button' $tone='revise' disabled={disabled} onClick={onRevise}>
            {reviseLabel}
        </BannerChip>
        <BannerChip type='button' $tone='confirm' disabled={disabled} onClick={onConfirm}>
            {confirmLabel}
        </BannerChip>
    </BannerActions>
);

export interface AiApprovalBannerProps {
    title: string;
    message?: ReactNode;
    reviseLabel: string;
    confirmLabel: string;
    onRevise: () => void;
    onConfirm: () => void;
    disabled?: boolean;
    /** Hide the action chips (e.g. on a non-latest turn); the banner still explains the state. Default: true. */
    showActions?: boolean;
    /** Applied to the banner root, so callers can size it (e.g. width: 85%). */
    className?: string;
}

export const AiApprovalBanner: FC<AiApprovalBannerProps> = ({
    title,
    message,
    reviseLabel,
    confirmLabel,
    onRevise,
    onConfirm,
    disabled,
    showActions = true,
    className
}) => (
    <BannerCard className={className}>
        <BannerText>
            <strong>{title}</strong>
            {message != null && <span>{message}</span>}
        </BannerText>
        {showActions && (
            <AiApprovalActions reviseLabel={reviseLabel} confirmLabel={confirmLabel} onRevise={onRevise} onConfirm={onConfirm} disabled={disabled} />
        )}
    </BannerCard>
);
